import {
  MiraInsurer,
  MiraInsurerMoveTarget,
  MiraInsurerSave,
  MiraInsurerSpellingMove,
  MiraInsurerSuggestion,
  UnresolvedInsurerGroup,
} from "@/data/models/MiraInsurerAdministration";

/**
 * The logic behind the insurer screens of the IRES Admin Client (Track #2610040 P3), kept out of the
 * components so it can be tested without mounting anything. IRES Cloud is the authority
 * (`MiraInsurersForAdministration.ts`); these rules give the messages before a save is sent.
 */

/** A key is 2 to 12 capital letters or digits (Taimoor, 2026-10-29, #2610125), editable. */
export const MIRA_INSURER_KEY_PATTERN = /^[A-Z0-9]{2,12}$/;

/** The list's filters. */
export const MIRA_INSURER_FILTERS = ["ACTIVE", "RETIRED", "ALL"] as const;
export type MiraInsurerFilter = typeof MIRA_INSURER_FILTERS[number];

/**
 * IRES Cloud's `normaliseInsurerName`, mirrored: lower case, umlauts and ß folded, soft hyphens dropped,
 * every other non-alphanumeric run one space. Spellings are stored this way, so the form compares the
 * same way ("Zürich" and "zuerich" are one spelling).
 *
 * @param {string} raw - a typed name.
 * @returns {string} the normalised form.
 */
export function normaliseInsurerName(raw: string): string {
  return raw
    .normalize("NFKC")
    .replace(/­/g, "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** One spelling per line, normalised, without empties or repeats, sorted, as IRES Cloud stores them. */
export function parseSpellingLines(text: string): string[] {
  return [...new Set(text.split("\n").map(normaliseInsurerName).filter((line) => line !== ""))].sort();
}

/** The dialog's editable fields. Spellings and words are one per line. */
export interface MiraInsurerForm {
  /** The shown key, never the internal id. */
  code: string;
  shortName: string;
  name: string;
  spellingsText: string;
  wordsText: string;
  retired: boolean;
}

/** The form for an insurer, or an empty one for a new insurer. */
export function toInsurerForm(insurer: MiraInsurer | null): MiraInsurerForm {
  return {
    code: insurer?.code ?? "",
    shortName: insurer?.shortName ?? "",
    name: insurer?.name ?? "",
    spellingsText: (insurer?.spellings ?? []).join("\n"),
    wordsText: (insurer?.words ?? []).join("\n"),
    retired: insurer?.retired ?? false,
  };
}

/**
 * A free key for a new insurer, proposed from its name (#2610125): the name's first four letters or digits,
 * else its first three and a digit, else empty for the user to type. A short name is filled up with digits
 * ("WPV" becomes "WPV1"). Only a proposal: the field stays editable, and IRES Cloud refuses a taken key.
 *
 * @param {string} name - the insurer's name as typed.
 * @param {string[]} takenKeys - every shown key on the list, retired insurers' included.
 * @returns {string} a free key, or "" when none of the candidates is free.
 */
export function suggestInsurerKey(name: string, takenKeys: string[]): string {
  const letters = normaliseInsurerName(name).replace(/ /g, "").toUpperCase();
  if (letters === "") return "";
  const taken = new Set(takenKeys);
  const candidates = [
    (letters + "1111").slice(0, 4),
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => `${(letters + "11").slice(0, 3)}${digit}`),
  ];
  return candidates.find((candidate) => !taken.has(candidate)) ?? "";
}

/**
 * The form for a new insurer made from a review group's ticked spellings (#2610125, "Neuer Versicherer"
 * in Gruppe zuordnen): the most used spelling as both names and the key proposed from it, every ticked
 * spelling as its spellings, so one save creates the insurer and claims them. A taken-over suggestion
 * brings its own key and names; its key only while it is still free.
 *
 * @param {string[]} texts - the ticked spellings, the most used first.
 * @param {string[]} takenKeys - every shown key on the list.
 * @param {MiraInsurerSuggestion | null} suggestion - a NEW_INSURER suggestion being taken over.
 * @returns {MiraInsurerForm} the pre-filled form.
 */
export function newInsurerForm(texts: string[], takenKeys: string[], suggestion: MiraInsurerSuggestion | null = null): MiraInsurerForm {
  const first = (texts[0] ?? "").trim();
  const proposed = suggestion?.target === "NEW_INSURER" ? suggestion : null;
  const code = proposed?.key && !takenKeys.includes(proposed.key) ? proposed.key : suggestInsurerKey(proposed?.shortName ?? first, takenKeys);
  return {
    ...toInsurerForm(null),
    code,
    shortName: proposed?.shortName ?? first,
    name: proposed?.name ?? first,
    spellingsText: texts.join("\n"),
  };
}

/** Whether the form differs from the stored insurer, comparing spellings as IRES Cloud stores them. */
export function insurerFormChanged(stored: MiraInsurer | null, form: MiraInsurerForm): boolean {
  const before = toInsurerForm(stored);
  return (
    form.code.trim().toUpperCase() !== before.code ||
    form.shortName.trim() !== before.shortName ||
    form.name.trim() !== before.name ||
    parseSpellingLines(form.spellingsText).join("\n") !== parseSpellingLines(before.spellingsText).join("\n") ||
    parseSpellingLines(form.wordsText).join("\n") !== parseSpellingLines(before.wordsText).join("\n") ||
    form.retired !== before.retired
  );
}

/** A field's problem, as an i18n key below `admin.miraInsurers.fieldErrors`. */
export function insurerFieldError(field: "code" | "shortName" | "name", form: MiraInsurerForm): string | null {
  if (field === "code") return MIRA_INSURER_KEY_PATTERN.test(form.code.trim().toUpperCase()) ? null : "key";
  return form[field].trim() === "" ? "required" : null;
}

/** Whether the form can be saved: a valid key and both names. */
export function canSaveInsurerForm(form: MiraInsurerForm): boolean {
  return (["code", "shortName", "name"] as const).every((field) => insurerFieldError(field, form) === null);
}

/** What a save sends. */
export function toInsurerSave(stored: MiraInsurer | null, form: MiraInsurerForm): MiraInsurerSave {
  return {
    create: stored === null,
    key: stored?.key ?? null,
    code: form.code.trim().toUpperCase(),
    shortName: form.shortName.trim(),
    name: form.name.trim(),
    spellings: parseSpellingLines(form.spellingsText),
    words: parseSpellingLines(form.wordsText),
    retired: form.retired,
    expectedUpdatedAt: stored?.updatedAt ?? null,
  };
}

/** The list rows for a filter and a search over key, names and spellings, by short name. */
export function filterInsurers(insurers: MiraInsurer[], filter: MiraInsurerFilter, search: string): MiraInsurer[] {
  const term = normaliseInsurerName(search);
  return insurers
    .filter((insurer) => (filter === "ALL" ? true : filter === "RETIRED" ? insurer.retired : !insurer.retired))
    .filter((insurer) =>
      !term ||
      [insurer.code, insurer.mdisKey ?? "", insurer.shortName, insurer.name, ...insurer.spellings, ...insurer.words]
        .some((value) => normaliseInsurerName(value).includes(term))
    )
    .sort((a, b) => a.shortName.localeCompare(b.shortName, "de"));
}

/** Where the move dialog sends the ticked spellings and words (#2610125, part 2). */
export type MiraInsurerMoveKind = MiraInsurerMoveTarget["kind"];

/** The move dialog's choices. */
export interface MiraInsurerMoveForm {
  spellings: string[];
  words: string[];
  kind: MiraInsurerMoveKind;
  /** INSURER: the target's internal id. */
  insurerKey: string | null;
  /** NEW_INSURER: the new insurer's key and names. */
  code: string;
  shortName: string;
  name: string;
}

/**
 * The move a form sends, or null while it cannot be sent: nothing ticked, no target insurer, an invalid
 * new insurer, or a word for Kein Versicherer (that list holds exact spellings only).
 *
 * @param {MiraInsurer} source - the insurer the spellings and words leave.
 * @param {MiraInsurerMoveForm} form - the dialog's choices.
 * @returns {MiraInsurerSpellingMove | null} the request.
 */
export function spellingMoveRequest(source: MiraInsurer, form: MiraInsurerMoveForm): MiraInsurerSpellingMove | null {
  if (!form.spellings.length && !form.words.length) return null;
  let to: MiraInsurerMoveTarget;
  if (form.kind === "INSURER") {
    if (!form.insurerKey || form.insurerKey === source.key) return null;
    to = { kind: "INSURER", key: form.insurerKey };
  } else if (form.kind === "NEW_INSURER") {
    const code = form.code.trim().toUpperCase();
    if (!MIRA_INSURER_KEY_PATTERN.test(code) || !form.shortName.trim() || !form.name.trim()) return null;
    to = { kind: "NEW_INSURER", code, shortName: form.shortName.trim(), name: form.name.trim() };
  } else if (form.kind === "NOT_INSURER") {
    if (form.words.length) return null;
    to = { kind: "NOT_INSURER" };
  } else {
    to = { kind: "RELEASE" };
  }
  return { key: source.key, expectedUpdatedAt: source.updatedAt, spellings: [...form.spellings], words: [...form.words], to };
}

/** The review groups Mira has a suggestion for (#2610125, the "Mit Vorschlag" view). */
export function groupsWithSuggestion(groups: UnresolvedInsurerGroup[]): UnresolvedInsurerGroup[] {
  return groups.filter((group) => group.suggestion !== undefined);
}

/**
 * The snackbar message for a refused or failed dismissal of a suggestion, by IRES Cloud's reason.
 *
 * @param {unknown} error - the rejected callable.
 * @returns {InsurerErrorMessage} the message.
 */
export function insurerSuggestionErrorMessage(error: unknown): InsurerErrorMessage {
  const reason = (error as { details?: { reason?: unknown } | null } | null)?.details?.reason;
  const known = ["NO_SUGGESTION", "CHANGED_MEANWHILE", "INVALID_REASON"];
  return {
    key: `admin.miraInsurerReview.suggestion.errors.${typeof reason === "string" && known.includes(reason) ? reason : "FAILED"}`,
    params: [],
  };
}

/** The review groups matching a search over their spellings. */
export function filterUnresolvedGroups(groups: UnresolvedInsurerGroup[], search: string): UnresolvedInsurerGroup[] {
  const term = normaliseInsurerName(search);
  return term ? groups.filter((group) => group.spellings.some((spelling) => normaliseInsurerName(spelling.text).includes(term))) : groups;
}

/** A snackbar message: an i18n key and its parameters. */
export interface InsurerErrorMessage {
  key: string;
  params: string[];
}

/**
 * The snackbar message for a refused or failed call, by IRES Cloud's reason. A clash names the first
 * spelling and its owner (`__none` is the "not an insurer" decision).
 *
 * @param {unknown} error - the rejected callable.
 * @returns {InsurerErrorMessage} the message.
 */
export function insurerErrorMessage(error: unknown): InsurerErrorMessage {
  const failure = (error ?? {}) as { details?: { reason?: unknown; conflicts?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  const conflicts = failure.details?.conflicts;
  if (reason === "CONFLICT" && Array.isArray(conflicts) && conflicts.length > 0) {
    const first = conflicts[0] as { spelling?: unknown; keys?: unknown };
    const keys = Array.isArray(first.keys) ? first.keys.filter((key): key is string => typeof key === "string") : [];
    return {
      key: keys.includes("__none") ? "admin.miraInsurers.errors.CONFLICT_NOT_INSURER" : "admin.miraInsurers.errors.CONFLICT",
      params: [String(first.spelling ?? ""), keys.filter((key) => key !== "__none").join(", ")],
    };
  }
  const known = ["KEY_TAKEN", "NO_INSURER", "CHANGED_MEANWHILE", "INVALID_KEY", "INVALID_NAME", "INVALID_SPELLINGS", "INVALID_TEXTS", "INVALID_MERGE"];
  return { key: known.includes(reason) ? `admin.miraInsurers.errors.${reason}` : "admin.miraInsurers.errors.FAILED", params: [] };
}
